import { createFileRoute } from '@tanstack/react-router';
import Logo from '@/assets/logo.svg';
import { PasswordCriteria } from '@/components/auth/PasswordCriteria';
import LoginHero from '@/components/auth/LoginHero';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

// Define the schema with Zod
const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' })
      .regex(/[A-Z]/, { message: 'Password must contain an uppercase letter' })
      .regex(/[a-z]/, { message: 'Password must contain a lowercase letter' })
      .regex(/[0-9]/, { message: 'Password must contain a number' })
      .regex(/[^A-Za-z0-9]/, {
        message: 'Password must contain a special character',
      }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

function ResetPassword() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const navigate = useNavigate();

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  // Watch password and confirmPassword for the criteria component
  const watchedPassword = form.watch('password') || '';
  const watchedConfirmPassword = form.watch('confirmPassword') || '';

  // Check if the user has a valid recovery session
  useEffect(() => {
    const checkRecoverySession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        setIsRecoverySession(true);
      } else {
        // If there's no session, redirect to login after a delay
        setTimeout(() => {
          navigate({ to: '/login' });
        }, 2000);
      }
    };

    checkRecoverySession();
  }, [navigate]);

  const handleSubmit = async (data: ResetPasswordFormValues) => {
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) throw error;

      setSuccess(true);

      // Redirect to home after successful password reset
      setTimeout(() => {
        navigate({ to: '/' });
      }, 2000);
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (!isRecoverySession && !success) {
    return (
      <div className="dark relative min-h-screen overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 w-full h-full">
          <svg
            className="absolute inset-0 w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1920 1080"
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <radialGradient id="bgGradient" cx="50%" cy="50%" r="70%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#1E1527" />
                <stop offset="70%" stopColor="#0D0910" />
                <stop offset="100%" stopColor="#000000" />
              </radialGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#bgGradient)" />
          </svg>
        </div>
        <div className="relative z-10 text-center text-muted-foreground">
          <p>Invalid or expired reset link. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dark relative min-h-screen overflow-hidden">
      {/* SVG Background */}
      <div className="absolute inset-0 w-full h-full">
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1920 1080"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <radialGradient id="bgGradient" cx="50%" cy="50%" r="70%" fx="50%" fy="50%">
              <stop offset="0%" stopColor="#1E1527" />
              <stop offset="70%" stopColor="#0D0910" />
              <stop offset="100%" stopColor="#000000" />
            </radialGradient>

            <filter id="purpleGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>

            <pattern id="dotPattern" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="15" cy="15" r="0.5" fill="#9B74F2" opacity="0.3" />
            </pattern>
          </defs>

          <rect width="100%" height="100%" fill="url(#bgGradient)" />
          <rect width="100%" height="100%" fill="url(#dotPattern)" />
        </svg>
      </div>

      <div className="flex flex-col md:flex-row min-h-screen relative z-10">
        {/* Left Column - Login Hero */}
        <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12 bg-black/30 backdrop-blur-sm">
          <LoginHero />
        </div>

        {/* Right Column - Reset Password Form */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12 bg-white/[0.02] backdrop-blur-sm"
        >
          <Card className="w-full max-w-md border-0 shadow-lg">
            <CardContent className="pt-8">
              {!success ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="space-y-4 text-center mb-8"
                  >
                    <img
                      src={Logo}
                      alt="Hooklistener"
                      className="h-8 w-auto mx-auto"
                    />
                    <div className="space-y-2">
                      <h1 className="text-2xl font-semibold tracking-tight">
                        <span className="text-white">Reset your password</span>
                      </h1>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        className="text-sm text-muted-foreground"
                      >
                        Enter your new password below
                      </motion.p>
                    </div>
                  </motion.div>

                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label
                        htmlFor="password"
                        className="text-sm font-medium"
                      >
                        New Password
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        className="h-11"
                        togglePasswordVisibility={true}
                        {...form.register('password')}
                        aria-invalid={
                          form.formState.errors.password ? 'true' : 'false'
                        }
                      />
                      {form.formState.errors.password && (
                        <p className="text-xs text-destructive mt-1">
                          {form.formState.errors.password.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="confirmPassword"
                        className="text-sm font-medium"
                      >
                        Confirm New Password
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        className="h-11"
                        togglePasswordVisibility={true}
                        {...form.register('confirmPassword')}
                        aria-invalid={
                          form.formState.errors.confirmPassword
                            ? 'true'
                            : 'false'
                        }
                      />
                      {form.formState.errors.confirmPassword && (
                        <p className="text-xs text-destructive mt-1">
                          {form.formState.errors.confirmPassword.message}
                        </p>
                      )}
                    </div>

                    <PasswordCriteria
                      password={watchedPassword}
                      confirmPassword={watchedConfirmPassword}
                    />

                    {error && (
                      <p className="text-sm text-destructive text-center">
                        {error}
                      </p>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-11 font-medium"
                      disabled={loading || !form.formState.isValid}
                    >
                      {loading ? 'Resetting password...' : 'Reset password'}
                    </Button>

                    <p className="text-sm text-muted-foreground text-center">
                      Remember your password?{' '}
                      <button
                        type="button"
                        onClick={() => navigate({ to: '/login' })}
                        className="text-primary hover:underline font-medium"
                      >
                        Sign in
                      </button>
                    </p>
                  </form>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center justify-center py-12 space-y-6"
                >
                  {/* Success Icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 200,
                      damping: 15,
                      delay: 0.2,
                    }}
                    className="relative w-20 h-20"
                  >
                    <div className="absolute inset-0 bg-linear-to-br from-green-400 to-green-600 rounded-full opacity-20 blur-xl" />
                    <div className="relative flex items-center justify-center w-full h-full bg-linear-to-br from-green-500/30 to-green-600/30 backdrop-blur-xs rounded-full border-2 border-green-500/50">
                      <Check className="w-10 h-10 text-green-500" />
                    </div>
                  </motion.div>

                  {/* Success Message */}
                  <div className="text-center space-y-2">
                    <h2 className="text-xl font-semibold text-white">
                      Password reset successfully!
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Redirecting you to the app...
                    </p>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/reset-password')({
  component: ResetPassword,
});
